import { isNodeVisible, type CompiledForm, type CompiledNode, type FillSubject } from '@record-fill';
import type { LabGrant, LabRule } from './useMatrixFillLab';

export type RuleAction = LabRule['action'];

export interface RuleEffect {
  action: RuleAction;
  targetFieldIds: string[];
}

export type TraceTargetState = 'visível' | 'oculto' | 'desabilitado' | 'ausente';

export interface RuleTraceTarget {
  id: string;
  isField: boolean;
  state: TraceTargetState;
}

export interface RuleTraceEffect {
  action: RuleAction;
  targets: RuleTraceTarget[];
}

export interface RuleTraceEntry {
  id: string;
  name: string;
  triggerFieldId: string;
  triggerOption: string;
  fires: boolean;
  effects: RuleTraceEffect[];
}

export interface GrantResolution {
  fieldId: string;
  allowed: boolean;
  positionIds: number[];
  functionalActivityIds: number[];
}

// The builder stores a rule's effects in thenBlocks; the flat action and targetFieldIds are
// the older shape, still used when no block carries an action. Same reading as the engine.
export function ruleEffects(rule: LabRule): RuleEffect[] {
  const blocks = rule.thenBlocks.filter((block) => block.action !== '');
  if (blocks.length > 0) {
    return blocks.map((block) => ({
      action: block.action as RuleAction,
      targetFieldIds: block.targets.flatMap((target) => target.fieldIds),
    }));
  }
  return [{ action: rule.action, targetFieldIds: rule.targetFieldIds }];
}

function* walkNodes(nodes: CompiledNode[]): Generator<CompiledNode> {
  for (const node of nodes) {
    yield node;
    if (node.kind !== 'table') continue;
    for (const row of node.cells) {
      for (const cell of row) {
        if (cell.node) yield* walkNodes([cell.node]);
      }
    }
  }
}

function fieldState(state: { visible: boolean; enabled: boolean }): TraceTargetState {
  if (!state.visible) return 'oculto';
  return state.enabled ? 'visível' : 'desabilitado';
}

function targetState(form: CompiledForm, id: string): RuleTraceTarget {
  const field = form.fields[id];
  if (field) {
    return { id, isField: true, state: fieldState(field.state) };
  }
  for (const node of walkNodes(form.nodes)) {
    if (node.id !== id) continue;
    return { id, isField: false, state: isNodeVisible(node) ? 'visível' : 'oculto' };
  }
  return { id, isField: false, state: 'ausente' };
}

export function buildRuleTrace(
  rules: LabRule[],
  form: CompiledForm,
  fires: (rule: LabRule) => boolean,
): RuleTraceEntry[] {
  return rules.map((rule) => ({
    id: rule.id,
    name: rule.name,
    triggerFieldId: rule.triggerFieldId,
    triggerOption: rule.triggerOption,
    fires: fires(rule),
    effects: ruleEffects(rule).map((effect) => ({
      action: effect.action,
      targets: effect.targetFieldIds.map((id) => targetState(form, id)),
    })),
  }));
}

function intersects(left: number[], right: number[]): boolean {
  const lookup = new Set(left);
  return right.some((id) => lookup.has(id));
}

export function resolveGrants(grants: LabGrant[], subject: FillSubject): GrantResolution[] {
  return grants.map((grant) => ({
    fieldId: grant.fieldId,
    allowed:
      intersects(grant.positionIds, subject.positionIds) ||
      intersects(grant.functionalActivityIds, subject.functionalActivityIds),
    positionIds: grant.positionIds,
    functionalActivityIds: grant.functionalActivityIds,
  }));
}
